import * as Sentry from '@sentry/browser';
import type { BrowserOptions } from '@sentry/browser';
import { installAnonymousTimerDiagnostics } from './anonymousTimerDiagnostics';
import { applyClientFingerprintTags } from './clientFingerprint';
import { isDevelopmentEnv } from './index';
import { getSentryDSN, getMCOEnvironment, getAppName } from './metaTags';
import safeCall from './safeCall';

// Expose sentry to the global scope allowing it to be called via console.
declare global {
  interface Window {
    __SENTRY_RSP__: typeof Sentry;
  }
}
window.__SENTRY_RSP__ = Sentry;

const appName = getAppName();
const environment = getMCOEnvironment();

/**
 * Drops non-actionable, high-volume noise before it ships to Sentry.
 * Exported for unit testing. Returns the event to keep it, or `null` to
 * discard it.
 */
export const beforeSend: NonNullable<BrowserOptions['beforeSend']> = (
  event,
  hint,
) => {
  const value =
    event?.exception?.values?.[0]?.value ||
    event?.message ||
    String(hint?.originalException ?? '');

  const msg = String(value);

  if (/^\[esri\./i.test(msg)) {
    return null;
  }

  if (/Attempted to load a Vector Map, but failed/i.test(msg)) {
    return null;
  }

  // deck.gl IconLayer / RSOSIconLayer sprite-atlas loads from the CDN
  // frequently fail with a transient network error. deck.gl logs these via
  // console.error (e.g. "deck: loading iconAtlas of RSOSIconLayer(...): Failed
  // to fetch (cdn-prod.rapidsos.com)"), and captureConsole turns each into a
  // Sentry error — thousands of events fragmented across dozens of layer ids.
  // They're not actionable; the atlas re-fetches on the next render. Match
  // every browser's network-failure wording (Chrome/Edge "Failed to fetch",
  // Firefox "NetworkError when attempting to fetch", Safari "Load failed"),
  // but stay gated on a network failure so a genuinely broken atlas or a
  // different deck.gl error (e.g. "Cannot read properties of null") still
  // surfaces.
  if (
    /deck: loading iconAtlas of/i.test(msg) &&
    /Failed to fetch|NetworkError when attempting to fetch|Load failed/i.test(
      msg,
    )
  ) {
    return null;
  }

  return event;
};

const baseOptions: BrowserOptions = {
  release: `${appName}@${process.env.REACT_APP_FE_VERSION}`,
  environment,
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ['error'], // Only capture console.error, not other console methods
    }),
  ],
  // Default is 3, which truncates nested objects passed as `extra` to
  // "[Object]". Allow a few more levels so error context (e.g. RTK Query
  // request payloads) is readable in the Sentry UI.
  normalizeDepth: 6,
  // replaysSessionSampleRate: environment === PRODUCTION_ENV ? 0.1 : 0, // https://docs.sentry.io/platforms/javascript/session-replay/
  // replaysOnErrorSampleRate: 0.1,
  // https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-platformidentifier-nameignore-errors-
  // https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-954602174
  ignoreErrors: ['Non-Error promise rejection captured'],

  beforeSend,
};

// Add DSN only in production environments per Sentry's documentation.
const options: BrowserOptions = isDevelopmentEnv()
  ? baseOptions
  : { ...baseOptions, dsn: getSentryDSN() };

// Initialize sentry the moment this module is executed.
safeCall(() => Sentry.init(options))();

// Tag the FE application name into the global scope.
if (appName) {
  safeCall(() => Sentry.getGlobalScope().setTag('fe-app', appName))();
}

/**
 * Sets a tag in the global scope. Tags are key/value pairs that allow for
 * additional data to be associated with the events being tracked.
 * Good use-cases: tracking language, local timezone, etc.
 * @param key - A value to be used as key for the tag
 * @param value - Value for the tag
 */
export const setGlobalTag = safeCall(
  (key: string, value: string | number | boolean): void => {
    Sentry.getGlobalScope().setTag(key, value);
  },
);

/**
 * User object interface for Sentry scope configuration
 */
export interface SentryUser {
  /** Optional, but expected by Sentry */
  id?: string;
  /** Optional, but expected by Sentry */
  username?: string;
  /** Optional, but expected by Sentry */
  email?: string;
  /** Additional user properties */
  [key: string]: unknown;
}

/**
 * Sets the user object in Sentry's global scope, which is
 * passed along with every request.
 * @param user - User object containing id, username, email, etc.
 */
export const configureUserScope = safeCall((user?: SentryUser): void => {
  if (!user) {
    return;
  }
  Sentry.getGlobalScope().setUser({
    ...user,
  });
});

/**
 * Clears the global scope.
 */
export const clearGlobalScope = safeCall((): void => {
  Sentry.getGlobalScope().clear();
});

/**
 * Captures the given exception and sets the extra properties
 * as part of the scope for the current exception.
 * @param error - The error to capture
 * @param extra - Additional context data
 * @param fingerprint - Optional grouping key. Exceptions group by stack trace
 *   by default, so callers that capture from a single shared location (e.g.
 *   a middleware) must pass a fingerprint to keep distinct failures in
 *   separate Sentry issues.
 * @param tags - Optional indexed key/value pairs. Sentry indexes tags but not
 *   `extra`, so anything you need to search, filter or break down an issue by
 *   (e.g. "what share of these events were offline?") belongs here. Keep the
 *   value set small — bucket counts and durations rather than passing raw
 *   numbers, and never pass ids.
 */
export const captureExceptionWithScope = safeCall(
  (
    error: Error | string,
    extra: Record<string, unknown> = {},
    fingerprint?: string[],
    tags: Record<string, string> = {},
  ): void => {
    if (isDevelopmentEnv()) {
      // eslint-disable-next-line no-console
      console.error('[DEV] captureExceptionWithScope:', error, extra, tags);
    }
    Sentry.withScope(scope => {
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Object.keys(tags).forEach(key => {
        scope.setTag(key, tags[key]);
      });
      if (fingerprint) {
        scope.setFingerprint(fingerprint);
      }
      Sentry.captureException(error);
    });
  },
);

/**
 * Captures the error without any extra scopes. Passes along
 * any globally defined scopes from initialization.
 * @param error - The error to capture
 */
export const captureException = safeCall((error: Error | string): void => {
  if (isDevelopmentEnv()) {
    // eslint-disable-next-line no-console
    console.error('[DEV] captureException:', error);
  }
  Sentry.captureException(error);
});

// Both of these run after `Sentry.init` above, and the timer diagnostics also
// have to run after `captureExceptionWithScope` exists. Sentry's
// BrowserApiErrors integration patches the same timer APIs during init, so
// installing later leaves our wrapper on the inside, holding the caller's
// original callback rather than Sentry's wrapper.
safeCall(() =>
  applyClientFingerprintTags((key: string, value: string) =>
    Sentry.getGlobalScope().setTag(key, value),
  ),
)();

safeCall(() => installAnonymousTimerDiagnostics(captureExceptionWithScope))();
